import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  CreditCard,
  CheckCircle2,
  Clock,
  Ban,
  XCircle,
  BookOpen,
  AlertTriangle,
  FileText,
  Loader2,
  RefreshCw,
  Copy,
  X,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { circulationApi, type FineRecord, type PaginatedResponse } from "@/lib/api/circulation";
import { invoicesApi, type Invoice } from "@/lib/api/invoices";
import { patronFineAmount } from "@/components/invoice/InvoiceHelpers";

const statusConfig: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
  PENDING: { label: "Unpaid", cls: "bg-amber-100 text-amber-700", icon: Clock },
  PAID: { label: "Paid", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  WAIVED: { label: "Waived", cls: "bg-blue-100 text-blue-600", icon: Ban },
  CANCELLED: { label: "Cancelled", cls: "bg-gray-100 text-gray-500", icon: XCircle },
};

type TabFilter = "unpaid" | "all";

const MyFinesPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<PaginatedResponse<FineRecord> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabFilter>("unpaid");
  const [page, setPage] = useState(1);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payingFineId, setPayingFineId] = useState<string | null>(null);
  const [verifyingFineId, setVerifyingFineId] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<{
    fineId: string;
    status: string;
    message: string;
    reference?: string;
  } | null>(null);

  useEffect(() => {
    if (user?.id) loadFines();
  }, [user?.id, tab, page]);

  const loadFines = async () => {
    setLoading(true);
    try {
      const status = tab === "unpaid" ? "PENDING" : undefined;
      const [result, invRes] = await Promise.all([
        circulationApi.getFines({ userId: user?.id, status, page, limit: 20 }),
        invoicesApi.list({ limit: 200 }),
      ]);
      setData(result);
      setInvoices(invRes.invoices);
    } catch {
      toast.error("Failed to load your fines");
    } finally {
      setLoading(false);
    }
  };

  const fineInvoiceMap = new Map<string, Invoice>();
  for (const inv of invoices) {
    if (inv.type === "fine" && inv.status === "pending" && inv.metadata?.fineId) {
      fineInvoiceMap.set(inv.metadata.fineId as string, inv);
    }
  }

  const handlePayFine = async (fineId: string) => {
    const invoice = fineInvoiceMap.get(fineId);
    if (!invoice) {
      toast.error("No payable invoice found for this fine. Please contact staff.");
      return;
    }
    setPayingFineId(fineId);
    try {
      const { authorizationUrl } = await invoicesApi.initiatePayment(invoice.id);
      window.location.href = authorizationUrl;
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to initiate payment");
      setPayingFineId(null);
    }
  };

  const handleVerifyPayment = async (fineId: string) => {
    const invoice = fineInvoiceMap.get(fineId);
    if (!invoice) {
      toast.error("No invoice found for this fine.");
      return;
    }
    setVerifyingFineId(fineId);
    setVerificationResult(null);
    try {
      const result = await invoicesApi.verifyPayment(invoice.id);
      if (result.success) {
        toast.success(result.message);
        setVerificationResult(null);
        loadFines();
      } else {
        setVerificationResult({
          fineId,
          status: result.status ?? "unknown",
          message: result.message,
          reference: result.reference,
        });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Verification failed");
    } finally {
      setVerifyingFineId(null);
    }
  };

  const amountFor = (fine: FineRecord) =>
    patronFineAmount(fine.amount, fineInvoiceMap.get(fine.id));

  const fines = data?.data ?? [];
  const totalUnpaid = fines
    .filter((f) => f.status === "PENDING")
    .reduce((sum, f) => sum + amountFor(f), 0);

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="My Fines"
          description="View your fines and make payments"
          breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "My Fines" }]}
          showSearch={false}
          titleSize="md"
        />

        {/* Summary card */}
        {totalUnpaid > 0 && (
          <Card className="p-4 border-amber-200 bg-amber-50/50">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800">
                    Outstanding Balance: ₦{totalUnpaid.toFixed(2)}
                  </p>
                  <p className="text-sm text-amber-700">
                    Unpaid fines may block new checkouts. Use the Pay buttons below to settle each
                    fine.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex bg-muted/50 rounded-lg p-1 gap-1 w-fit">
          {(["unpaid", "all"] as TabFilter[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setPage(1);
              }}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                tab === t
                  ? "bg-background shadow font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "unpaid" ? "Unpaid" : "All Fines"}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : fines.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500 opacity-70" />
            <p className="font-medium">
              {tab === "unpaid" ? "No unpaid fines" : "No fine history"}
            </p>
            <p className="text-sm mt-1">
              {tab === "unpaid"
                ? "Your account is in good standing."
                : "You have no fines on record."}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {fines.map((fine) => {
              const cfg = statusConfig[fine.status] ?? statusConfig.PENDING;
              const StatusIcon = cfg.icon;
              const hasPayableInvoice = fineInvoiceMap.has(fine.id);

              return (
                <Card
                  key={fine.id}
                  className={`p-4 ${fine.status === "PENDING" ? "border-amber-200" : ""}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cfg.cls}`}
                    >
                      <StatusIcon className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-sm">{fine.reason}</h3>
                          {fine.checkout?.item?.biblio?.title && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                              <BookOpen className="w-3 h-3" />
                              {fine.checkout.item.biblio.title}
                            </p>
                          )}
                          {fine.notes && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <FileText className="w-3 h-3 shrink-0" />
                              {fine.notes}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p
                            className={`font-bold ${fine.status === "PENDING" ? "text-amber-700" : "text-foreground"}`}
                          >
                            ₦{amountFor(fine).toFixed(2)}
                          </p>
                          <Badge variant="secondary" className={`${cfg.cls} text-xs mt-1`}>
                            {cfg.label}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                        <span>Created: {new Date(fine.createdAt).toLocaleDateString()}</span>
                        {fine.paidAt && (
                          <span className="text-green-600">
                            Paid: {new Date(fine.paidAt).toLocaleDateString()}
                          </span>
                        )}
                        {fine.waivedAt && (
                          <span className="text-blue-600">
                            Waived: {new Date(fine.waivedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {fine.status === "PENDING" && hasPayableInvoice && (
                        <div className="mt-3 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVerifyPayment(fine.id)}
                            disabled={verifyingFineId === fine.id}
                            title="Use if you paid but it still shows unpaid (e.g. session expired on redirect)"
                          >
                            {verifyingFineId === fine.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-1" />
                            )}
                            {verifyingFineId === fine.id ? "Checking..." : "Verify payment"}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handlePayFine(fine.id)}
                            disabled={payingFineId === fine.id}
                          >
                            {payingFineId === fine.id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Redirecting...
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-1" />
                                Pay ₦{amountFor(fine).toFixed(2)}
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {verificationResult?.fineId === fine.id && (
                        <Alert variant="destructive" className="mt-3">
                          <div className="flex items-start justify-between gap-2">
                            <AlertDescription className="flex-1">
                              <span className="font-medium">
                                Status: {verificationResult.status.toUpperCase()}
                              </span>
                              {" · "}
                              {verificationResult.message}
                              {verificationResult.reference && (
                                <span className="block mt-2 font-mono text-sm">
                                  Reference: {verificationResult.reference}
                                </span>
                              )}
                            </AlertDescription>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="shrink-0 h-6 w-6 p-0"
                              onClick={() => setVerificationResult(null)}
                              aria-label="Dismiss"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          {verificationResult.reference && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2 w-fit"
                              onClick={() => {
                                navigator.clipboard.writeText(verificationResult.reference!);
                                toast.success("Reference copied");
                              }}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy reference
                            </Button>
                          )}
                        </Alert>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {data.page} of {data.totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default MyFinesPage;
